import Radio from "backbone.radio";
import strings from "common/strings";
import sweetalert from "sweetalert";
import { toast } from "extensions/toast";

import { store } from "common/store";
import {
    hashStringToColor,
    destroyPopovers
} from "common/utils";

import { authentication, merchant, user as userAPI, users as usersAPI } from "common/config/api";

import { request } from "common/components/request";
import { showExportDialog } from "merchant/features/downloads/exportDialog/exportDialog";
import { DO_MIMIC_USER } from "common/actions/authentication";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/pro-regular-svg-icons";

export const MAKE_USER_OBJECT = (user) => ({
    ...user,
    background: hashStringToColor(user.username || ""), // It can be null when we don't know who is the user, e.g. sign in page
    worldpay_rm: user.merchant_ref === store.getState().config.worldpayMerchant,
    letter: user.first_name?.[0] || user.last_name?.[0] || "?"
});

export const EXPORT_USERS = function(user_ref) {
    const exportOptions = {
        formats: ["xlsx"],
        activeFormat: "xlsx",
        resources: [{ value: "users_management", label: "Users" }],
        activeResource: "users_management"
    };

    const merchant_ref = store.getState().common.merchant_ref;
    const display_name = store.getState().common.merchant_config.display_name;

    if (user_ref) {
        const user = store.getState().common.merchant_users[user_ref];

        Object.assign(exportOptions, {
            dialogTitle: "Export user",
            filename: `${display_name}-${user.first_name}_${user.last_name}`,
            content: [
                {
                    value: "user_profile",
                    label: "User profile",
                    value_payload: {
                        merchant_ref,
                        user_ref
                    }
                }
            ]
        });
    } else {
        Object.assign(exportOptions, {
            dialogTitle: "Export users",
            filename: `${display_name}-users`,
            content: [
                {
                    value: "users_list",
                    label: "Users list",
                    value_payload: {
                        merchant_ref
                    }
                },
                {
                    value: "users_permissions",
                    label: "Permissions",
                    value_payload: {
                        merchant_ref
                    }
                },
                {
                    value: "groups",
                    label: "Groups",
                    value_payload: {
                        merchant_ref
                    }
                },
                {
                    value: "users_groups",
                    label: "Users by groups",
                    value_payload: {
                        merchant_ref
                    }
                }
            ],
            activeContent: ["users_list", "users_permissions", "groups", "users_groups"]
        });
    }

    showExportDialog(exportOptions);
};

export const UNLOCK_USER = function(userId) {
    const user = store.getState().common.merchant_users[userId];

    return sweetalert({
        title: "Are you sure you want to unlock this user?",
        icon: "warning",
        buttons: {
            confirm: {
                text: "Unlock user",
                className: "btn-confirm"
            },
            cancel: {
                visible: true
            }
        }
    }).then((confirmed) => {
        if (confirmed) {
            return request(authentication.unlock, {
                data: {
                    id: userId
                },
                method: "POST"
            }).then(() => {
                store.dispatch({
                    type: "UPDATE_MERCHANT_USER",
                    id: user._id,
                    data: {
                        locked: false
                    }
                });

                toast.success((
                    <div className="toast-with-actions">
                        <div>{user.first_name} {user.last_name} has been unlocked.</div>
                        {!user.sso_user && (
                            <a
                                href="#"
                                className="toast-action"
                                onClick={(e) => {
                                    e.preventDefault();
                                    RESET_USER_PASSWORD(user._id);
                                }}
                            >
                                Send a password reset email
                            </a>
                        )}
                    </div>
                ));
            }, (error) => {
                toast.error("There was an error unlocking a user");
                return Promise.reject(error);
            }).finally(() => {
                destroyPopovers();
            });
        }
    });
};

// New action to replace ACTIVATE/DEACTIVATE_USER
// This action rely on redux store
export const CHANGE_USER_ACTIVE_STATE = function(userId, new_state) {
    const state = store.getState();
    const myself = state.common.user;
    const { can_manage_service_portal_users } = state.permissions.user_permissions;
    const user = state.common.merchant_users[userId];

    if (!myself.superuser && (user.sso_user && !can_manage_service_portal_users)) {
        return sweetalert({
            title: new_state ? "You cannot activate an SSO user" : "You cannot deactivate an SSO user",
            icon: "warning",
            buttons: {
                confirm: {
                    text: "OK, got it",
                    className: "btn-confirm"
                }
            }
        }).then(() => Promise.reject());
    }

    if (user._id === myself._id) {
        return sweetalert({
            title: "You cannot select yourself for any changes.",
            icon: "warning",
            buttons: {
                confirm: {
                    text: "OK, got it",
                    className: "btn-confirm"
                }
            }
        }).then(() => Promise.reject());
    }

    return request(new_state ? authentication.activate : authentication.deactivate, {
        data: { id: user._id },
        method: "POST"
    }).then((res) => {
        toast.success(`${user.first_name} ${user.last_name} has been ${new_state ? "activated" : "deactivated"}`);
        store.dispatch({
            type: "UPDATE_MERCHANT_USER",
            id: user._id,
            data: {
                active: new_state
            }
        });
    }, (error) => {
        toast.error("There was an error requesting user change.");
        return Promise.reject(error);
    }).finally(() => {
        destroyPopovers();
    });
};

export const MIMIC_USER = function(userId) {
    const { superuser } = store.getState().common.merchant_users[userId];

    if (superuser) {
        sweetalert({
            title: "Cannot mimic a superuser",
            icon: "warning",
            buttons: {
                confirm: {
                    text: "OK, got it",
                    className: "btn-confirm"
                }
            }
        });
        return this;
    }

    sweetalert({
        title: "Are you sure you want to mimic this user?",
        icon: "warning",
        buttons: {
            confirm: {
                text: "Mimic this user",
                className: "btn-confirm"
            },
            cancel: {
                visible: true
            }
        }
    }).then((confirmed) => {
        if (confirmed) {
            // Request a new token for the user id we should mimic
            store.dispatch(DO_MIMIC_USER(userId));
        }
    });
};

export const RESET_USER_PASSWORD = function(userId) {
    const user = store.getState().common.merchant_users[userId];

    sweetalert({
        title: "Are you sure you want to reset password for this user?",
        icon: "warning",
        buttons: {
            confirm: {
                text: "Reset password",
                className: "btn-confirm"
            },
            cancel: {
                visible: true
            }
        }
    }).then((confirmed) => {
        if (confirmed) {
            return request(authentication.password.reset, {
                method: "POST",
                data: {
                    email: user.username
                },
                ensureAuth: false
            }).then(() => {
                toast.success(`Password is reset for ${user.first_name} ${user.last_name}. Email sent successfully.`);
            }, () => {
                toast.error("There was an error requesting password reset.");
            });
        }
    });
};

export const SEND_ACTIVATION_EMAIL = function(userId) {
    const user = store.getState().common.merchant_users[userId];

    sweetalert({
        title: "Are you sure you want to send welcome email to this user?",
        icon: "warning",
        buttons: {
            confirm: {
                text: "Send",
                className: "btn-confirm"
            },
            cancel: {
                visible: true
            }
        }
    }).then((confirmed) => {
        if (confirmed) {
            return request(authentication.sendActivationEmail, {
                method: "POST",
                data: {
                    email: user.username
                }
            }).then(() => {
                toast.success(`Activation email with instructions to ${user.first_name} ${user.last_name} sent successfully.`);
            }, () => {
                toast.error("There was an error sending activation email.");
            });
        }
    });
};

export const EMAIL_ADMINS_FOR_PERMISSIONS = function(userID) {
    return request("/merchant/:merchant_ref/user_requires_permissions_email", {
        method: "POST",
        data: {
            user_ref: userID
        }
    }).then(() => true, () => {
        toast.error("There was an error sending your email request.");
    });
};

export const GET_USERS_WITH_NO_PERMISSIONS = () => request("/merchant/:merchant_ref/users_require_permissions").then((data) => {
    store.dispatch({
        type: "STORE_NEEDY_MERCHANT_USERS_FOR_ADMIN",
        data
    });
    
    return data;
});

export const FETCH_MERCHANT_USERS = (data = {}) => (dispatch) => request(merchant.users, {
    data
}).then(
    (res) => {
        const users = res.reduce((memo, usr) => ({
            ...memo,
            [usr._id]: usr
        }), Object.create(null));

        dispatch({
            type: "LOAD_MERCHANT_USERS",
            data: users
        });

        return res;
    }
);

export const FETCH_MERCHANT_GROUPS = () => (dispatch) => request(merchant.groups).then(
    (groups) => {
        dispatch({
            type: "LOAD_MERCHANT_USER_GROUPS",
            data: groups || []
        });
    }
);

export const FETCH_MERCHANT_PERMISSIONS = () => (dispatch) => request(merchant.permissions).then(
    (res) => {
        dispatch({
            type: "permissions/LOAD_MERCHANT_PERMISSIONS",
            data: res || {}
        });
    }
);

export const FETCH_USER_PERMISSIONS_ON_MERCHANT = () => {
    const packageOverride = localStorage.getItem("package-override");
    const params = {};
    const state = store.getState();
    const isAdminSuperuserWorldpayRm = state.common.user.superuser || state.common.user.admin || state.common.user.merchant_ref === "5807bdd3bf7ce77b20816ad5";
    const userTypePermissions = {
        has_page_development: state.common.user.superuser,
        has_data_status_actions: state.common.user.superuser,
        can_deactivate_sso_users: state.common.user.superuser,
        can_change_password: !state.common.user.sso_user,
        can_preview_download: state.common.user.superuser,
        can_add_threshold: state.common.user.superuser,
        has_package_demo: state.common.user.switch_packages,
        has_reminders: state.common.user.superuser || state.common.user.admin,
        has_bu_export: isAdminSuperuserWorldpayRm,
        has_bu_manager: isAdminSuperuserWorldpayRm,
        has_users_needing_permissions_data: isAdminSuperuserWorldpayRm,
        has_report_file_download: state.common.user.superuser,
        feature_psd2_new: state.common.user.superuser
    };

    if (packageOverride) {
        if (packageOverride === "Lite") {
            params.package = state.common.merchant_config.package_ref;
        }

        if (packageOverride === "demo_Lite") {
            params.package = state.common.mappings.packages.find(({ name, partner }) => name === "lite" && partner === "worldpay")?._id;
        }

        if (packageOverride === "demo_Pro") {
            params.package = state.common.mappings.packages.find(({ name, partner }) => name === "pro" && partner === "worldpay")?._id;
        }

        if (packageOverride === "demo_Enterprise") {
            params.package = state.common.mappings.packages.find(({ name, partner }) => name === "enterprise" && partner === "worldpay")?._id;
        }

        if (packageOverride === "demo_All") {
            // Noop
        }
    }

    return request(merchant.user_permissions, {
        data: params
    }).then(
        (data) => {
            store.dispatch({
                type: "permissions/LOAD_USER_MERCHANT_PERMISSIONS",
                data,
                userTypePermissions
            });

            return data;
        }
    );
};

export const FETCH_USER_LOGIN_HISTORY = (id) => request(usersAPI.login_history(id));

export const GROUP_DELETE = (groupId) => request(`/merchant/:merchant_ref/groups/${groupId}`, {
    method: "DELETE"
}).then(() => {
    store.dispatch({
        type: "GROUP_DELETE_SUCCESS",
        groupId
    });
});

export const GROUP_CREATE = (group) => request("/merchant/:merchant_ref/groups", {
    method: "POST",
    data: group
}).then((data) => {
    store.dispatch({
        type: "GROUP_CREATE_SUCCESS",
        data
    });

    return data;
});

export const GROUP_UPDATE = (group) => request(`/merchant/:merchant_ref/groups/${group.id}`, {
    method: "PUT",
    data: group
}).then((data) => {
    store.dispatch({
        type: "GROUP_UPDATE_SUCCESS",
        group
    });

    return group;
});

export const GROUP_SET_DEFAULT = (groupId) => {
    const state = store.getState();
    const prev_id = state.common.merchant_config.default_group;
    const prev_group = state.common.merchant_groups.find((group) => group.id === prev_id);
    const new_group = state.common.merchant_groups.find((group) => group.id === groupId);

    let stringTemplate = ({ next }) => `You are about to change the default group to "${next}"`;
    if (groupId === null || groupId === prev_id) {
        stringTemplate = ({ previous }) => `You are about to remove default group "${previous}"`;
        groupId = null;
    } else if (prev_id) {
        stringTemplate = ({ previous, next }) => `You are about to change the default group from "${previous}" to "${next}"`;
    }

    return sweetalert({
        title: stringTemplate({
            next: new_group?.display_name,
            previous: prev_group?.display_name
        }),
        icon: "warning",
        buttons: {
            confirm: {
                text: strings.continue,
                className: "btn-confirm"
            },
            cancel: {
                visible: true
            }
        }
    }).then((confirmed) => {
        if (confirmed) {
            return Radio.channel("main").request("get:merchant").proxyTo.save({
                default_group: groupId
            }, {
                error() {
                    toast.error("Error setting a default group");
                }
            }).then(() => {
                store.dispatch({
                    type: "GROUP_UPDATE_DEFAULT",
                    groupId
                });
            });
        }

        return Promise.reject(new Error("Canceled"));
    });
};

export const SAVE_USER_PROFILE = function(data) {
    return (dispatch, getState) => {
        const cleanData = {
            employee_id: (data.employee_id) ? data.employee_id.trim() : "",
            first_name: data.first_name.trim(),
            last_name: data.last_name.trim(),
            job_title: data.job_title.trim(),
            username: getState().permissions.user_permissions.can_update_email ? data.username.trim() : getState().common.user.username
        };

        return request("/users/me", {
            method: "PUT",
            data: cleanData
        }).then(() => {
            dispatch({
                type: "USER_UPDATE_PROFILE_SUCCESS",
                data: cleanData
            });
            toast.success("Your profile changes have been saved.");
        }).catch((error) => {
            toast.error("There was an error saving changes to your profile.");
        });
    };
};

export const SET_USER_PROFILE_MID_EMAIL = function(emailOptOut) {
    return (dispatch, getState) => request("/users/me", {
        method: "PUT",
        data: emailOptOut
    }).then(() => {
        dispatch({
            type: "USER_UPDATE_PROFILE_SUCCESS",
            data: emailOptOut
        });
    });
};

const feature_names = {
    FeeReports: "feeReports"
};

export const FETCH_USER_OPTINS = function() {
    return (dispatch) => {
        request(userAPI.optIns).then((res) => {
            const feature_map = {};
            res.forEach((feature) => {
                feature.feature = feature_names[feature.feature];
                feature_map[feature.feature] = feature;
            });
            store.dispatch({
                type: "SET_USER_OPTINS",
                optIns: feature_map
            });
        }).catch((error) => {
            console.log("There was an error loading optins");
        });
    };
};

export const SET_USER_FEE_REPORTS = function(feeReports) {
    return (dispatch) => {
        request(userAPI.optIns, {
            method: "PUT",
            data: { feature: "FeeReports", context: feeReports.context, opt_out: !feeReports.active }
        }).then((res) => {
            if (feeReports.active) {
                const toast_message = (
                    <div>
                        <FontAwesomeIcon icon={faCheck}/>&nbsp;
                        Request confirmed! Someone from the Worldpay Corporate Support team
                        will reach out within 5 working days to confirm your preferences.
                    </div>
                );
                toast.success(toast_message, { autoClose: false });
            } else {
                const toast_message = (
                    <div>
                        <FontAwesomeIcon icon={faCheck}/>&nbsp;
                        You have requested to stop receiving fee detail reports from Worldpay.
                        Your request will be actioned within 5 working days, however please be aware that this
                        may not take effect until the next Billing month cycle.
                    </div>
                );
                toast.success(toast_message, { autoClose: false });
            }
            store.dispatch({
                type: "SET_USER_FEE_REPORTS",
                feeReports
            });
        }).catch((error) => {
            console.log(error);
            toast.error("Error updating Fee reports");
        });
    };
};
